// Прогресс игрока по карте 45 точек (W2: было 50). Singleton — общий между сценами.
// Персистент: load/saveToProvider через SaveProvider (Telegram CloudStorage / localStorage).
// Транзакция: takeSnapshot перед боем → коммит при победе (clearSnapshot + save), откат при
// проигрыше (restoreFromSnapshot). Закрытие вкладки посреди боя → следующий заход с диска
// = последняя успешная фиксация.

import { MAX_LEVEL } from '../constants/progression/LevelConfig';
import { getUnlockedWeapons } from '../constants/progression/WeaponUnlocks';
import type { SaveProvider } from './save/SaveProvider';

export interface MapProgressData {
  highestUnlockedLevel: number;
  completedLevels: number[];
  money: number;
  // Купленные/полученные — сохраняются между боями. revolver всегда тут (стартом).
  ownedWeapons: string[];
}

const STARTING_OWNED = ['revolver'];

function makeDefaultData(): MapProgressData {
  return {
    highestUnlockedLevel: 1,
    completedLevels: [],
    money: 0,
    ownedWeapons: [...STARTING_OWNED],
  };
}

function cloneData(data: MapProgressData): MapProgressData {
  // Deep copy — массивы должны быть независимы (иначе мутация .push сломает снапшот).
  // MapProgressData = plain JSON (числа/строки/массивы примитивов), JSON-roundtrip ок.
  return JSON.parse(JSON.stringify(data)) as MapProgressData;
}

export class MapProgress {
  private data: MapProgressData = makeDefaultData();
  // Снапшот точки отката (создаётся ПЕРЕД входом в уровень). null = транзакция не открыта.
  private snapshot: MapProgressData | null = null;

  getData(): MapProgressData {
    return this.data;
  }

  // Открыть транзакцию: запомнить состояние до боя для возможного отката.
  takeSnapshot(): void {
    this.snapshot = cloneData(this.data);
  }

  // Откатиться к снапшоту (при проигрыше). Снапшот остаётся — на случай retry той же точки.
  restoreFromSnapshot(): void {
    if (!this.snapshot) {
      console.warn('[MapProgress] restoreFromSnapshot: no snapshot');
      return;
    }
    this.data = cloneData(this.snapshot);
  }

  // Закрыть транзакцию (после коммита — победа на уровне).
  clearSnapshot(): void {
    this.snapshot = null;
  }

  // Загрузить с диска (через провайдер). Вызывается на старте игры до показа карты.
  async loadFromProvider(provider: SaveProvider): Promise<void> {
    try {
      const loaded = await provider.load();
      if (loaded) {
        this.data = loaded;
        // Старое поле unlockedWeapons (до W13) могло остаться в сейве —
        // вычищаем, чтобы не таскать мёртвые байты в storage. isWeaponUnlocked
        // теперь считает unlocked на лету из completedLevels + текущих правил.
        delete (this.data as Partial<MapProgressData> & { unlockedWeapons?: string[] })
          .unlockedWeapons;
      }
      // null → новый игрок, остаёмся с дефолтом.
    } catch (err) {
      console.warn('[MapProgress] load failed, using default:', err);
    }
  }

  // Сохранить на диск (fire-and-forget из вызывающего кода; ошибки логируем).
  async saveToProvider(provider: SaveProvider): Promise<void> {
    try {
      await provider.save(this.data);
    } catch (err) {
      console.warn('[MapProgress] save failed:', err);
    }
  }

  isLevelUnlocked(level: number): boolean {
    return level <= this.data.highestUnlockedLevel;
  }

  // Следующая точка для шорткат-кнопки «В БОЙ»: первая открытая И ещё не
  // пройденная. Игра линейная (highestUnlockedLevel = lastCompleted + 1), так
  // что в норме это просто highestUnlockedLevel. null = вся карта пройдена.
  getNextPlayableLevel(): number | null {
    for (let level = 1; level <= this.data.highestUnlockedLevel; level++) {
      if (!this.data.completedLevels.includes(level)) return level;
    }
    return null;
  }

  isLevelCompleted(level: number): boolean {
    return this.data.completedLevels.includes(level);
  }

  completeLevel(level: number): void {
    if (!this.data.completedLevels.includes(level)) {
      this.data.completedLevels.push(level);
    }
    if (level + 1 > this.data.highestUnlockedLevel && level < MAX_LEVEL) {
      this.data.highestUnlockedLevel = level + 1;
    }
    // unlockedWeapons теперь геттер на лету через isWeaponUnlocked — пересчитывать не надо.
  }

  addMoney(amount: number): void {
    this.data.money += amount;
  }

  spendMoney(amount: number): void {
    this.data.money = Math.max(0, this.data.money - amount);
  }

  isWeaponUnlocked(weaponId: string): boolean {
    // Считаем разблокировку на лету из completedLevels + текущих правил
    // WEAPON_UNLOCKS. Так перенастройка правил (баланс, своп оружий) сразу
    // применяется к существующим сохранениям без миграции.
    return getUnlockedWeapons(this.data.completedLevels).includes(weaponId);
  }

  isWeaponOwned(weaponId: string): boolean {
    return this.data.ownedWeapons.includes(weaponId);
  }

  addOwnedWeapon(weaponId: string): void {
    if (!this.data.ownedWeapons.includes(weaponId)) {
      this.data.ownedWeapons.push(weaponId);
    }
  }

  unlockAll(): void {
    this.data.highestUnlockedLevel = MAX_LEVEL;
  }

  reset(): void {
    this.data = makeDefaultData();
    this.snapshot = null;
  }
}

// Module-level singleton — общий между MapScene и GameScene.
export const mapProgress = new MapProgress();
